import React, { useState, useEffect, useRef, useMemo } from 'react';
import Modal from 'react-modal';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import HeaderUno from '../Metodos/Compartidos/HeaderUno';
import HeaderDos from '../Metodos/Compartidos/HeaderDos';
import useConfigAuth from '../../Hooks/useConfigAuth';

import { Cuerpo } from '../Css/StyledHeader.js';

import {
  clinicalHistoryModalStyles,
  ClinicalHistoryPage,
  ClinicalHistoryCard,
  ClinicalHistoryInfoBar,
  ClinicalFiltersBar,
  ClinicalFilterGroup,
  ClinicalFilterLabel,
  ClinicalFilterSelect,
  ClinicalFilterInput,
  ClinicalClearFiltersButton,
  SessionIcon,
  ClinicalTableCard,
  TableScroll,
  ClinicalTable,
  DateCell,
  RowAccent,
  SessionNumber,
  TypeChip,
  AssistanceText,
  ActivityChip,
  ObservationText,
  StatusBadge,
  EmptyState,
  ClinicalModalCard,
  ClinicalModalCloseRow,
  ClinicalModalCloseButton,
  ClinicalModalTitle,
  ClinicalModalHeading,
  ClinicalModalGrid,
  ClinicalModalField,
  ClinicalModalLabel,
  ClinicalModalReadOnly,
  ClinicalModalTextarea,
  ClinicalModalActions,
  ClinicalSaveButton,
  ClinicalModalHeader,
  ClinicalModalHeaderTop,
  ClinicalModalPatientName,
  ClinicalModalMetaRow,
  ClinicalModalMetaItem,
  ClinicalModalHeaderPill,
  ClinicalModalBody,
  ClinicalModalNotice,
  ClinicalModalSectionLabel,
  ClinicalModalInput,
  ClinicalModalFooter,
  ClinicalModalFooterHint,
  ClinicalCancelButton,
} from '../Css/StyledHistoriaClinica.js';

import {
  historiaClinicaSolicitarAction,
  modificarHistoriaClinicaAction
} from '../../actions/historiaClinicaActions';

Modal.setAppElement('#root');

const SESSION_TYPE_META = {
  CONSULTORIO: {
    key: 'CONSULTORIO',
    label: 'Sesión con paciente',
    variant: 'teal',
    icon: '▰'
  },
  DOMICILIO: {
    key: 'DOMICILIO',
    label: 'Actividades en domicilio',
    variant: 'purple',
    icon: '⌂'
  },
  REUNION_COLEGIO: {
    key: 'REUNION_COLEGIO',
    label: 'Reunión con colegio',
    variant: 'orange',
    icon: '↗'
  },
  REUNION_PADRES: {
    key: 'REUNION_PADRES',
    label: 'Reunión con padres',
    variant: 'pink',
    icon: '♟'
  },
  REUNION_PROFESIONALES: {
    key: 'REUNION_PROFESIONALES',
    label: 'Reunión con profesionales',
    variant: 'green',
    icon: '△'
  }
};

const ESTADO_FILTER_OPTIONS = [
  {
    value: '',
    label: 'Cualquier estado'
  },
  {
    value: 'REALIZADO',
    label: 'Realizado / presente'
  },
  {
    value: 'NO_REALIZADO',
    label: 'No asistió / no realizada'
  },
  {
    value: 'PENDIENTE',
    label: 'Pendiente'
  },
  {
    value: 'CANCELADO',
    label: 'Cancelado'
  },
  {
    value: 'REPROGRAMADO',
    label: 'Reprogramado'
  }
];

const safeLower = (value) => {
  return String(value || '').trim().toLowerCase();
};

const safeUpper = (value) => {
  return String(value || '').trim().toUpperCase();
};

const getFechaSesion = (sesion) => {
  return (
    sesion?.fecha ||
    sesion?.fecha_turno ||
    sesion?.fecha_agendada ||
    ''
  );
};

const parseFechaParaFiltro = (fecha) => {
  if (!fecha) {
    return null;
  }

  const fechaString = String(fecha).trim();

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(fechaString)) {
    const [day, month, year] = fechaString.split('/').map(Number);
    return new Date(year, month - 1, day);
  }

  const fechaIso = fechaString.slice(0, 10);

  if (/^\d{4}-\d{2}-\d{2}$/.test(fechaIso)) {
    const [year, month, day] = fechaIso.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  return null;
};

const getMonthKeyFromDate = (date) => {
  if (!date) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');

  return `${year}-${month}`;
};

const formatMonthLabel = (date) => {
  if (!date) {
    return '';
  }

  return new Intl.DateTimeFormat('es-AR', {
    month: 'long',
    year: 'numeric'
  }).format(date);
};

const formatFechaParaMostrar = (fecha) => {
  if (!fecha) {
    return '—';
  }

  const fechaString = String(fecha).trim();

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(fechaString)) {
    return fechaString;
  }

  const fechaIso = fechaString.slice(0, 10);

  if (/^\d{4}-\d{2}-\d{2}$/.test(fechaIso)) {
    const [year, month, day] = fechaIso.split('-');
    return `${day}/${month}/${year}`;
  }

  return fechaString;
};

const parseDatosEspecificos = (sesion) => {
  const datos = sesion?.datos_especificos_json;

  if (!datos) {
    return {};
  }

  if (typeof datos === 'object') {
    return datos;
  }

  try {
    return JSON.parse(datos);
  } catch (error) {
    return {};
  }
};

const getSessionTypeMeta = (sesion) => {
  const tipoEntrada = safeUpper(
    sesion?.tipo_entrada ||
    sesion?.tipo_turno ||
    sesion?.tipo
  );

  const lugar = safeLower(sesion?.lugar);
  const ejercitacion = safeLower(sesion?.ejercitacion);

  if (
    tipoEntrada === 'REUNION_PADRES' ||
    tipoEntrada.includes('PADRES')
  ) {
    return SESSION_TYPE_META.REUNION_PADRES;
  }

  if (
    tipoEntrada === 'REUNION_COLEGIO' ||
    tipoEntrada.includes('COLEGIO')
  ) {
    return SESSION_TYPE_META.REUNION_COLEGIO;
  }

  if (
    tipoEntrada === 'REUNION_PROFESIONALES' ||
    tipoEntrada.includes('PROFESIONAL')
  ) {
    return SESSION_TYPE_META.REUNION_PROFESIONALES;
  }

  if (
    lugar.includes('domicilio') ||
    ejercitacion.includes('sin profesional') ||
    tipoEntrada.includes('DOMICILIO')
  ) {
    return SESSION_TYPE_META.DOMICILIO;
  }

  return SESSION_TYPE_META.CONSULTORIO;
};

const getDatosEspecificosIniciales = (sesion) => {
  const datos = parseDatosEspecificos(sesion);
  const sessionType = getSessionTypeMeta(sesion);

  if (sessionType.key === 'REUNION_COLEGIO') {
    return {
      colegio: datos.colegio || datos.institucion || '',
      curso_grado: datos.curso_grado || datos.curso || datos.grado || '',
      participantes: datos.participantes || '',
      temas_tratados_acuerdos:
        datos.temas_tratados_acuerdos ||
        datos.temas_tratados ||
        '',
      observaciones: datos.observaciones || ''
    };
  }

  if (sessionType.key === 'REUNION_PADRES') {
    return {
      participantes: datos.participantes || '',
      temas_tratados_acuerdos:
        datos.temas_tratados_acuerdos ||
        datos.temas_tratados ||
        '',
      observaciones: datos.observaciones || ''
    };
  }

  if (sessionType.key === 'REUNION_PROFESIONALES') {
    return {
      profesionales_participantes:
        datos.profesionales_participantes ||
        datos.participantes ||
        '',
      temas_tratados_acuerdos:
        datos.temas_tratados_acuerdos ||
        datos.temas_tratados ||
        '',
      observaciones: datos.observaciones || ''
    };
  }

  return {
    observaciones: datos.observaciones || ''
  };
};

const getEstadoFiltroKey = (sesion) => {
  const estado = safeLower(sesion?.estado);
  const asistencia = safeLower(sesion?.asistencia);

  if (
    estado.includes('cancel') ||
    asistencia.includes('cancel')
  ) {
    return 'CANCELADO';
  }

  if (
    estado.includes('reprogram') ||
    asistencia.includes('reprogram')
  ) {
    return 'REPROGRAMADO';
  }

  if (
    estado.includes('no_asist') ||
    estado.includes('no asist') ||
    estado.includes('no_realiz') ||
    estado.includes('no realiz') ||
    asistencia.includes('ausente') ||
    asistencia.includes('no asist')
  ) {
    return 'NO_REALIZADO';
  }

  if (
    estado.includes('realiz') ||
    estado.includes('complet') ||
    estado.includes('revis') ||
    asistencia.includes('presente')
  ) {
    return 'REALIZADO';
  }

  return 'PENDIENTE';
};

const getAsistenciaMeta = (sesion) => {
  const asistencia = safeLower(sesion?.asistencia);
  const estadoFiltro = getEstadoFiltroKey(sesion);

  if (asistencia.includes('presente')) {
    return {
      label: 'Presente',
      status: 'presente'
    };
  }

  if (
    asistencia.includes('ausente') ||
    asistencia.includes('no asist')
  ) {
    return {
      label: 'Ausente',
      status: 'ausente'
    };
  }

  if (estadoFiltro === 'REALIZADO') {
    return {
      label: 'Realizado',
      status: 'presente'
    };
  }

  if (estadoFiltro === 'NO_REALIZADO') {
    return {
      label: 'No asistió',
      status: 'ausente'
    };
  }

  if (estadoFiltro === 'CANCELADO') {
    return {
      label: 'Cancelado',
      status: 'na'
    };
  }

  if (estadoFiltro === 'REPROGRAMADO') {
    return {
      label: 'Reprogramado',
      status: 'na'
    };
  }

  if (!asistencia) {
    return {
      label: '—',
      status: 'na'
    };
  }

  return {
    label: sesion.asistencia,
    status: 'na'
  };
};

const getEstadoMeta = (sesion) => {
  const estadoFiltro = getEstadoFiltroKey(sesion);

  if (estadoFiltro === 'REALIZADO') {
    return {
      label: 'realizado',
      status: 'realizado'
    };
  }

  if (estadoFiltro === 'NO_REALIZADO') {
    return {
      label: 'no asistió',
      status: 'no_realizado'
    };
  }

  if (estadoFiltro === 'CANCELADO') {
    return {
      label: 'cancelado',
      status: 'cancelado'
    };
  }

  if (estadoFiltro === 'REPROGRAMADO') {
    return {
      label: 'reprogramado',
      status: 'reprogramado'
    };
  }

  const estado = safeLower(sesion?.estado);

  if (estado === 'incompleto') {
    return {
      label: 'incompleto',
      status: 'incompleto'
    };
  }

  return {
    label: 'pendiente',
    status: 'pendiente'
  };
};

const getActividadPrincipal = (sesion) => {
  const datosEspecificos = parseDatosEspecificos(sesion);

  if (sesion?.actividades) {
    return sesion.actividades;
  }

  if (datosEspecificos.temas_tratados_acuerdos) {
    return datosEspecificos.temas_tratados_acuerdos;
  }

  if (datosEspecificos.temas_tratados) {
    return datosEspecificos.temas_tratados;
  }

  if (datosEspecificos.actividades) {
    return datosEspecificos.actividades;
  }

  return '—';
};

const getObservacionPrincipal = (sesion) => {
  const datosEspecificos = parseDatosEspecificos(sesion);
  const sessionType = getSessionTypeMeta(sesion);

  if (
    sessionType.key === 'CONSULTORIO' ||
    sessionType.key === 'DOMICILIO'
  ) {
    if (sesion?.comentario) {
      return sesion.comentario;
    }

    if (datosEspecificos.observaciones) {
      return datosEspecificos.observaciones;
    }

    return '—';
  }

  if (datosEspecificos.observaciones) {
    return datosEspecificos.observaciones;
  }

  if (datosEspecificos.participantes) {
    return `Participantes: ${datosEspecificos.participantes}`;
  }

  if (datosEspecificos.profesionales_participantes) {
    return `Profesionales: ${datosEspecificos.profesionales_participantes}`;
  }

  if (sesion?.comentario) {
    return sesion.comentario;
  }

  return '—';
};

const getHoraSesion = (sesion) => {
  if (sesion?.hora_inicio && sesion?.hora_fin) {
    return `${sesion.hora_inicio} a ${sesion.hora_fin}`;
  }

  if (sesion?.hora_inicio) {
    return sesion.hora_inicio;
  }

  return '—';
};

const getLugarSesion = (sesion) => {
  if (sesion?.consultorio_nombre) {
    return sesion.consultorio_nombre;
  }

  if (sesion?.lugar) {
    return sesion.lugar;
  }

  if (safeLower(sesion?.modalidad_turno) === 'virtual') {
    return 'Virtual';
  }

  return '—';
};

const getModalidadSesion = (sesion) => {
  const modalidad = safeLower(
    sesion?.modalidad_turno ||
    sesion?.modalidad
  );

  if (modalidad === 'virtual') {
    return 'Virtual';
  }

  if (modalidad === 'presencial') {
    return 'Presencial';
  }

  return modalidad || '—';
};

const getProfesionalSesion = (sesion) => {
  return (
    sesion?.profesional_nombre ||
    sesion?.nombre_profesional ||
    sesion?.profesional ||
    '—'
  );
};

const getPacienteSesion = (sesion) => {
  const nombreCompleto =
    sesion?.paciente_nombre_completo ||
    `${sesion?.paciente_nombre || ''} ${sesion?.paciente_apellido || ''}`.trim() ||
    sesion?.paciente ||
    '';

  return nombreCompleto || 'Paciente';
};

const getComentarioInicial = (sesion) => {
  const datosEspecificos = parseDatosEspecificos(sesion);

  return (
    sesion?.comentario ||
    datosEspecificos.observaciones ||
    ''
  );
};

const getTextoBusquedaSesion = (sesion) => {
  const datosEspecificos = parseDatosEspecificos(sesion);
  const sessionType = getSessionTypeMeta(sesion);
  const asistencia = getAsistenciaMeta(sesion);
  const estado = getEstadoMeta(sesion);

  return [
    formatFechaParaMostrar(getFechaSesion(sesion)),
    sessionType.label,
    asistencia.label,
    estado.label,
    getActividadPrincipal(sesion),
    getObservacionPrincipal(sesion),
    getLugarSesion(sesion),
    getHoraSesion(sesion),
    datosEspecificos.colegio,
    datosEspecificos.curso_grado,
    datosEspecificos.participantes,
    datosEspecificos.profesionales_participantes,
    datosEspecificos.temas_tratados,
    datosEspecificos.temas_tratados_acuerdos,
    datosEspecificos.observaciones
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

const HistoriaClinica = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const [config] = useConfigAuth();

  const historiaClinicaRedux = useSelector(
    (state) => state.informacion.info_historia_clinica
  );

  const idPacienteRedux = useSelector((state) => state.usuario.id_paciente);

  const datosDesdeCalendario = location.state || {};

  const idPacienteDesdeCalendario =
    datosDesdeCalendario.id_paciente ||
    '';

  const historiaClinicaIdDesdeCalendario =
    datosDesdeCalendario.historia_clinica_id ||
    null;

  const idTurnoDesdeCalendario =
    datosDesdeCalendario.id_turno ||
    null;

  const vieneDesdeCalendario =
    datosDesdeCalendario.origen === 'CALENDARIO';

  const id_paciente = idPacienteDesdeCalendario || idPacienteRedux;

  const historiaClinica = useMemo(() => {
    return Array.isArray(historiaClinicaRedux)
      ? historiaClinicaRedux
      : [];
  }, [historiaClinicaRedux]);

  const historiaClinicaLista = useMemo(() => {
    return historiaClinica.filter((item) => item && item !== 'vacio');
  }, [historiaClinica]);

const [modalIsOpen, setModalIsOpen] = useState(false);
const [idSesion, setIdSesion] = useState('');
const [comentarioSesion, setComentarioSesion] = useState('');
const [datosEspecificosForm, setDatosEspecificosForm] = useState({});
const [cambio, setCambio] = useState(null);
const [cargandoHistoriaDesdeCalendario, setCargandoHistoriaDesdeCalendario] = useState(false);
const [guardandoHistoriaClinica, setGuardandoHistoriaClinica] = useState(false);

  const [filtros, setFiltros] = useState({
    mes: '',
    tipo: '',
    estado: '',
    fechaDesde: '',
    fechaHasta: '',
    texto: ''
  });

  const historiaClinicaSolicitadaRef = useRef(false);
  const historiaClinicaCalendarioAbiertaRef = useRef(false);

  const opcionesMes = useMemo(() => {
    const mesesMap = new Map();

    historiaClinicaLista.forEach((sesion) => {
      const fechaSesion = parseFechaParaFiltro(getFechaSesion(sesion));

      if (!fechaSesion) {
        return;
      }

      const monthKey = getMonthKeyFromDate(fechaSesion);

      if (!mesesMap.has(monthKey)) {
        mesesMap.set(monthKey, formatMonthLabel(fechaSesion));
      }
    });

    return Array.from(mesesMap.entries())
      .map(([value, label]) => ({
        value,
        label
      }))
      .sort((a, b) => b.value.localeCompare(a.value));
  }, [historiaClinicaLista]);

  const historiaClinicaFiltrada = useMemo(() => {
    const textoFiltro = filtros.texto.trim().toLowerCase();

    return historiaClinicaLista.filter((sesion) => {
      const fechaSesion = parseFechaParaFiltro(getFechaSesion(sesion));
      const sessionType = getSessionTypeMeta(sesion);
      const estadoFiltro = getEstadoFiltroKey(sesion);

      if (filtros.mes) {
        if (!fechaSesion || getMonthKeyFromDate(fechaSesion) !== filtros.mes) {
          return false;
        }
      }

      if (filtros.tipo && sessionType.key !== filtros.tipo) {
        return false;
      }

      if (filtros.estado && estadoFiltro !== filtros.estado) {
        return false;
      }

      if (filtros.fechaDesde) {
        const fechaDesde = parseFechaParaFiltro(filtros.fechaDesde);

        if (!fechaSesion || !fechaDesde || fechaSesion < fechaDesde) {
          return false;
        }
      }

      if (filtros.fechaHasta) {
        const fechaHasta = parseFechaParaFiltro(filtros.fechaHasta);

        if (!fechaSesion || !fechaHasta || fechaSesion > fechaHasta) {
          return false;
        }
      }

      if (textoFiltro) {
        const textoSesion = getTextoBusquedaSesion(sesion);

        if (!textoSesion.includes(textoFiltro)) {
          return false;
        }
      }

      return true;
    });
  }, [historiaClinicaLista, filtros]);

  const hayFiltrosActivos = Boolean(
    filtros.mes ||
    filtros.tipo ||
    filtros.estado ||
    filtros.fechaDesde ||
    filtros.fechaHasta ||
    filtros.texto
  );

  const actualizarFiltro = (campo, valor) => {
    setFiltros((prev) => ({
      ...prev,
      [campo]: valor
    }));
  };

  const limpiarFiltros = () => {
    setFiltros({
      mes: '',
      tipo: '',
      estado: '',
      fechaDesde: '',
      fechaHasta: '',
      texto: ''
    });
  };

  const actualizarDatoEspecifico = (campo, valor) => {
    setDatosEspecificosForm((prev) => ({
      ...prev,
      [campo]: valor
    }));
  };

  const abrirSesion = (sesion) => {
    if (!sesion) {
      return;
    }

    setIdSesion(sesion.id);
    setComentarioSesion(getComentarioInicial(sesion));
    setDatosEspecificosForm(getDatosEspecificosIniciales(sesion));
    setCambio(sesion);
    setModalIsOpen(true);
  };

  const cerrarModalEdicion = (forzarCierre = false) => {
    if (guardandoHistoriaClinica && !forzarCierre) {
      return;
    }

    setModalIsOpen(false);
    setIdSesion('');
    setComentarioSesion('');
    setDatosEspecificosForm({});
    setCambio(null);
  };

  const getComentarioParaGuardar = () => {
    if (!cambio) {
      return comentarioSesion || '';
    }

    const sessionType = getSessionTypeMeta(cambio);

    if (
      sessionType.key === 'REUNION_COLEGIO' ||
      sessionType.key === 'REUNION_PADRES' ||
      sessionType.key === 'REUNION_PROFESIONALES'
    ) {
      return (
        datosEspecificosForm.observaciones ||
        comentarioSesion ||
        ''
      );
    }

    return comentarioSesion || '';
  };

  const getDatosEspecificosParaGuardar = () => {
    if (!cambio) {
      return datosEspecificosForm || {};
    }

    const sessionType = getSessionTypeMeta(cambio);

    if (
      sessionType.key === 'CONSULTORIO' ||
      sessionType.key === 'DOMICILIO'
    ) {
      return {
        ...datosEspecificosForm,
        observaciones: comentarioSesion || datosEspecificosForm.observaciones || ''
      };
    }

    return {
      ...datosEspecificosForm
    };
  };

  const modificarSesion = async (e) => {
    e.preventDefault();

    if (guardandoHistoriaClinica) {
      return;
    }

    setGuardandoHistoriaClinica(true);

    const informacion = {
      config,
      payload: {
        id_sesion: idSesion,
        id_paciente: id_paciente,
        comentario: getComentarioParaGuardar(),
        datos_especificos_json: getDatosEspecificosParaGuardar()
      }
    };

    try {
      const guardadoCorrectamente = await dispatch(
        modificarHistoriaClinicaAction(informacion)
      );

      if (guardadoCorrectamente) {
        cerrarModalEdicion(true);
        return;
      }

      alert('No se pudo guardar la información clínica. Probá nuevamente.');

    } catch (error) {
      alert('No se pudo guardar la información clínica. Probá nuevamente.');
    } finally {
      setGuardandoHistoriaClinica(false);
    }
  };

  const renderInputDato = ({
    label,
    campo,
    placeholder = ''
  }) => {
    return (
      <ClinicalModalField>
        <ClinicalModalLabel htmlFor={`hc-${campo}`}>
          {label}
        </ClinicalModalLabel>

        <ClinicalModalInput
          id={`hc-${campo}`}
          type="text"
          value={datosEspecificosForm[campo] || ''}
          placeholder={placeholder}
          onChange={(e) => actualizarDatoEspecifico(campo, e.target.value)}
        />
      </ClinicalModalField>
    );
  };

  const renderTextareaDato = ({
    label,
    campo,
    placeholder = '',
    height = '110px'
  }) => {
    return (
      <ClinicalModalField style={{ marginTop: '12px' }}>
        <ClinicalModalLabel htmlFor={`hc-${campo}`}>
          {label}
        </ClinicalModalLabel>

        <ClinicalModalTextarea
          id={`hc-${campo}`}
          $height={height}
          value={datosEspecificosForm[campo] || ''}
          placeholder={placeholder}
          onChange={(e) => actualizarDatoEspecifico(campo, e.target.value)}
        />
      </ClinicalModalField>
    );
  };

  const renderCamposDinamicos = () => {
    if (!cambio) {
      return null;
    }

    const sessionType = getSessionTypeMeta(cambio);

    if (sessionType.key === 'REUNION_COLEGIO') {
      return (
        <>
          <ClinicalModalGrid>
            {renderInputDato({
              label: 'Colegio',
              campo: 'colegio',
              placeholder: 'Nombre de la institución'
            })}

            {renderInputDato({
              label: 'Curso / grado',
              campo: 'curso_grado',
              placeholder: 'Ej.: 3.º grado'
            })}

            {renderInputDato({
              label: 'Personas presentes',
              campo: 'participantes',
              placeholder: 'Ej.: docente, EOE, madre'
            })}
          </ClinicalModalGrid>

          {renderTextareaDato({
            label: 'Temas tratados / acuerdos',
            campo: 'temas_tratados_acuerdos',
            placeholder: 'Escribí lo conversado y los acuerdos alcanzados...',
            height: '130px'
          })}

          {renderTextareaDato({
            label: 'Observaciones',
            campo: 'observaciones',
            placeholder: 'Observaciones adicionales...',
            height: '82px'
          })}
        </>
      );
    }

    if (sessionType.key === 'REUNION_PADRES') {
      return (
        <>
          {renderInputDato({
            label: 'Personas presentes',
            campo: 'participantes',
            placeholder: 'Ej.: madre y padre / madre / padre / abuela'
          })}

          {renderTextareaDato({
            label: 'Temas tratados / acuerdos',
            campo: 'temas_tratados_acuerdos',
            placeholder: 'Escribí lo conversado y los acuerdos alcanzados...',
            height: '130px'
          })}

          {renderTextareaDato({
            label: 'Observaciones',
            campo: 'observaciones',
            placeholder: 'Observaciones adicionales...',
            height: '82px'
          })}
        </>
      );
    }

    if (sessionType.key === 'REUNION_PROFESIONALES') {
      return (
        <>
          {renderInputDato({
            label: 'Profesionales presentes',
            campo: 'profesionales_participantes',
            placeholder: 'Ej.: Lic. Ana Pérez – Psicología; Lic. Laura Gómez – Fonoaudiología'
          })}

          {renderTextareaDato({
            label: 'Temas tratados / acuerdos',
            campo: 'temas_tratados_acuerdos',
            placeholder: 'Escribí lo conversado y los acuerdos alcanzados...',
            height: '130px'
          })}

          {renderTextareaDato({
            label: 'Observaciones',
            campo: 'observaciones',
            placeholder: 'Observaciones adicionales...',
            height: '82px'
          })}
        </>
      );
    }

    if (sessionType.key === 'DOMICILIO') {
      return (
        <>
          <ClinicalModalField>
            <ClinicalModalLabel>
              Actividades asignadas
            </ClinicalModalLabel>

            <ClinicalModalTextarea
              $height="92px"
              disabled
              value={getActividadPrincipal(cambio)}
            />
          </ClinicalModalField>

          <ClinicalModalField style={{ marginTop: '12px' }}>
            <ClinicalModalLabel htmlFor="hc-comentario-domicilio">
              Observación
            </ClinicalModalLabel>

            <ClinicalModalTextarea
              id="hc-comentario-domicilio"
              $height="150px"
              name="comentarioSesion"
              value={comentarioSesion || ''}
              onChange={(e) => setComentarioSesion(e.target.value)}
              placeholder="Escribí una observación opcional..."
            />
          </ClinicalModalField>
        </>
      );
    }

    return (
      <>
        <ClinicalModalField>
          <ClinicalModalLabel>
            Actividades / acuerdos
          </ClinicalModalLabel>

          <ClinicalModalTextarea
            $height="92px"
            disabled
            value={getActividadPrincipal(cambio)}
          />
        </ClinicalModalField>

        <ClinicalModalField style={{ marginTop: '12px' }}>
          <ClinicalModalLabel htmlFor="hc-comentario-sesion">
            Comentario clínico
          </ClinicalModalLabel>

          <ClinicalModalTextarea
            id="hc-comentario-sesion"
            $height="180px"
            name="comentarioSesion"
            value={comentarioSesion || ''}
            onChange={(e) => setComentarioSesion(e.target.value)}
            placeholder="Escribí el comentario de esta sesión..."
          />
        </ClinicalModalField>
      </>
    );
  };

  useEffect(() => {
    if (!config || !id_paciente) {
      return;
    }

    const debeCargarPorCalendario = vieneDesdeCalendario;
    const debeCargarPorReduxVacio =
      !vieneDesdeCalendario &&
      historiaClinica.length === 0;

    if (!debeCargarPorCalendario && !debeCargarPorReduxVacio) {
      return;
    }

    if (historiaClinicaSolicitadaRef.current) {
      return;
    }

    historiaClinicaSolicitadaRef.current = true;
    setCargandoHistoriaDesdeCalendario(true);

    const informacion = {
      config,
      payload: {
        id_paciente
      }
    };

    Promise.resolve(dispatch(historiaClinicaSolicitarAction(informacion)))
      .finally(() => {
        setCargandoHistoriaDesdeCalendario(false);
      });
  }, [
    vieneDesdeCalendario,
    config,
    id_paciente,
    historiaClinica.length,
    dispatch
  ]);

  useEffect(() => {
    if (!idSesion) {
      return;
    }

    const sesionEncontrada = historiaClinicaLista.find((obj) => {
      return String(obj.id) === String(idSesion);
    });

    if (!sesionEncontrada) {
      return;
    }

    setComentarioSesion(getComentarioInicial(sesionEncontrada));
    setDatosEspecificosForm(getDatosEspecificosIniciales(sesionEncontrada));
    setCambio(sesionEncontrada);
  }, [idSesion, historiaClinicaLista]);

  useEffect(() => {
    if (!historiaClinicaIdDesdeCalendario) {
      return;
    }

    if (historiaClinicaCalendarioAbiertaRef.current) {
      return;
    }

    if (!Array.isArray(historiaClinicaLista) || historiaClinicaLista.length === 0) {
      return;
    }

    const historiaClinicaEncontrada = historiaClinicaLista.find((sesion) => {
      return (
        String(sesion.id) === String(historiaClinicaIdDesdeCalendario) ||
        String(sesion.historia_clinica_id) === String(historiaClinicaIdDesdeCalendario) ||
        String(sesion.hc_id) === String(historiaClinicaIdDesdeCalendario)
      );
    });

    if (!historiaClinicaEncontrada) {
      return;
    }

    historiaClinicaCalendarioAbiertaRef.current = true;
    abrirSesion(historiaClinicaEncontrada);
  }, [historiaClinicaIdDesdeCalendario, historiaClinicaLista]);

  return (
    <Cuerpo>
      <HeaderUno />

      <HeaderDos
        link={`/escritorio/pacientes/paciente`}
        componente={"Historia Clínica"}
        paciente={!vieneDesdeCalendario}
      />

      <ClinicalHistoryPage>
        <ClinicalHistoryCard>
          {vieneDesdeCalendario && (
            <ClinicalHistoryInfoBar>
              Venís desde Calendario.
              {idTurnoDesdeCalendario && (
                <>
                  {' '}
                  Turno asociado: <strong>{idTurnoDesdeCalendario}</strong>.
                </>
              )}
              {historiaClinicaIdDesdeCalendario && (
                <>
                  {' '}
                  Historia clínica: <strong>{historiaClinicaIdDesdeCalendario}</strong>.
                </>
              )}
            </ClinicalHistoryInfoBar>
          )}

          <ClinicalFiltersBar>
            <ClinicalFilterGroup>
              <ClinicalFilterLabel htmlFor="hc-filter-mes">
                Mes
              </ClinicalFilterLabel>

              <ClinicalFilterSelect
                id="hc-filter-mes"
                value={filtros.mes}
                onChange={(e) => actualizarFiltro('mes', e.target.value)}
              >
                <option value="">Todos los meses</option>

                {opcionesMes.map((mes) => (
                  <option key={mes.value} value={mes.value}>
                    {mes.label}
                  </option>
                ))}
              </ClinicalFilterSelect>
            </ClinicalFilterGroup>

            <ClinicalFilterGroup>
              <ClinicalFilterLabel htmlFor="hc-filter-tipo">
                Tipo
              </ClinicalFilterLabel>

              <ClinicalFilterSelect
                id="hc-filter-tipo"
                value={filtros.tipo}
                onChange={(e) => actualizarFiltro('tipo', e.target.value)}
              >
                <option value="">Todos los tipos</option>

                {Object.values(SESSION_TYPE_META).map((sessionType) => (
                  <option key={sessionType.key} value={sessionType.key}>
                    {sessionType.label}
                  </option>
                ))}
              </ClinicalFilterSelect>
            </ClinicalFilterGroup>

            <ClinicalFilterGroup>
              <ClinicalFilterLabel htmlFor="hc-filter-estado">
                Estado
              </ClinicalFilterLabel>

              <ClinicalFilterSelect
                id="hc-filter-estado"
                value={filtros.estado}
                onChange={(e) => actualizarFiltro('estado', e.target.value)}
              >
                {ESTADO_FILTER_OPTIONS.map((estadoOption) => (
                  <option key={estadoOption.value} value={estadoOption.value}>
                    {estadoOption.label}
                  </option>
                ))}
              </ClinicalFilterSelect>
            </ClinicalFilterGroup>

            <ClinicalFilterGroup>
              <ClinicalFilterLabel htmlFor="hc-filter-desde">
                Desde
              </ClinicalFilterLabel>

              <ClinicalFilterInput
                id="hc-filter-desde"
                type="date"
                value={filtros.fechaDesde}
                onChange={(e) => actualizarFiltro('fechaDesde', e.target.value)}
              />
            </ClinicalFilterGroup>

            <ClinicalFilterGroup>
              <ClinicalFilterLabel htmlFor="hc-filter-hasta">
                Hasta
              </ClinicalFilterLabel>

              <ClinicalFilterInput
                id="hc-filter-hasta"
                type="date"
                value={filtros.fechaHasta}
                onChange={(e) => actualizarFiltro('fechaHasta', e.target.value)}
              />
            </ClinicalFilterGroup>

            <ClinicalFilterGroup $grow>
              <ClinicalFilterLabel htmlFor="hc-filter-texto">
                Buscar
              </ClinicalFilterLabel>

              <ClinicalFilterInput
                id="hc-filter-texto"
                type="text"
                placeholder="Buscar por tema, actividad o comentario..."
                value={filtros.texto}
                onChange={(e) => actualizarFiltro('texto', e.target.value)}
              />
            </ClinicalFilterGroup>

            <ClinicalClearFiltersButton
              type="button"
              disabled={!hayFiltrosActivos}
              onClick={limpiarFiltros}
            >
              Limpiar
            </ClinicalClearFiltersButton>
          </ClinicalFiltersBar>

          <ClinicalTableCard>
            <TableScroll>
              <ClinicalTable>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Ses.</th>
                    <th>Tipo de entrada</th>
                    <th>Asistencia</th>
                    <th>Actividades</th>
                    <th>Estado</th>
                    <th>Comentario</th>
                  </tr>
                </thead>

                <tbody>
                  {historiaClinicaLista.length === 0 ? (
                    <tr>
                      <td colSpan={7}>
                        <EmptyState>
                          {cargandoHistoriaDesdeCalendario
                            ? 'Cargando historia clínica...'
                            : 'Todavía no hay entradas de historia clínica para mostrar.'}
                        </EmptyState>
                      </td>
                    </tr>
                  ) : historiaClinicaFiltrada.length === 0 ? (
                    <tr>
                      <td colSpan={7}>
                        <EmptyState>
                          No hay entradas que coincidan con los filtros seleccionados.
                        </EmptyState>
                      </td>
                    </tr>
                  ) : (
                    historiaClinicaFiltrada.map((sesionClinica) => {
                      const sessionType = getSessionTypeMeta(sesionClinica);
                      const asistencia = getAsistenciaMeta(sesionClinica);
                      const estado = getEstadoMeta(sesionClinica);
                      const actividad = getActividadPrincipal(sesionClinica);
                      const observacion = getObservacionPrincipal(sesionClinica);

                      return (
                        <tr
                          key={sesionClinica.id}
                          role="button"
                          tabIndex={0}
                          title="Abrir historia clínica"
                          onClick={() => abrirSesion(sesionClinica)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              abrirSesion(sesionClinica);
                            }
                          }}
                        >
                          <td>
                            <DateCell>
                              <RowAccent $variant={sessionType.variant} />
                              {formatFechaParaMostrar(getFechaSesion(sesionClinica))}
                            </DateCell>
                          </td>

                          <td>
                            <SessionNumber>
                              {sesionClinica.sesion || '—'}
                            </SessionNumber>
                          </td>

                          <td>
                            <TypeChip $variant={sessionType.variant}>
                              <SessionIcon>{sessionType.icon}</SessionIcon>
                              {sessionType.label}
                            </TypeChip>
                          </td>

                          <td>
                            <AssistanceText $status={asistencia.status}>
                              {asistencia.label}
                            </AssistanceText>
                          </td>

                          <td>
                            <ActivityChip
                              $variant={sessionType.variant}
                              title={actividad}
                            >
                              {actividad}
                            </ActivityChip>
                          </td>

                          <td>
                            <StatusBadge $status={estado.status}>
                              {estado.label}
                            </StatusBadge>
                          </td>

                          <td>
                            <ObservationText title={observacion}>
                              {observacion}
                            </ObservationText>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </ClinicalTable>
            </TableScroll>
          </ClinicalTableCard>
        </ClinicalHistoryCard>
      </ClinicalHistoryPage>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={cerrarModalEdicion}
        style={clinicalHistoryModalStyles}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={!guardandoHistoriaClinica}
        contentLabel="Editar historia clínica"
      >
        {cambio ? (
          <ClinicalModalCard>
            <ClinicalModalHeader $variant={getSessionTypeMeta(cambio).variant}>
              <ClinicalModalCloseRow>
                <ClinicalModalCloseButton
                  type="button"
                  onClick={cerrarModalEdicion}
                  disabled={guardandoHistoriaClinica}
                  aria-label="Cerrar"
                >
                  ×
                </ClinicalModalCloseButton>
              </ClinicalModalCloseRow>

              <ClinicalModalHeaderTop>
                <div>
                  <ClinicalModalPatientName>
                    {getPacienteSesion(cambio)}
                  </ClinicalModalPatientName>

                  <ClinicalModalHeaderPill>
                    <SessionIcon>{getSessionTypeMeta(cambio).icon}</SessionIcon>
                    {getSessionTypeMeta(cambio).label}
                  </ClinicalModalHeaderPill>
                </div>
              </ClinicalModalHeaderTop>

              <ClinicalModalMetaRow>
                <ClinicalModalMetaItem>
                  {formatFechaParaMostrar(getFechaSesion(cambio))}
                </ClinicalModalMetaItem>

                <ClinicalModalMetaItem>
                  ·
                </ClinicalModalMetaItem>

                <ClinicalModalMetaItem>
                  {getHoraSesion(cambio)}
                </ClinicalModalMetaItem>

                <ClinicalModalMetaItem>
                  ·
                </ClinicalModalMetaItem>

                <ClinicalModalMetaItem>
                  {getModalidadSesion(cambio)}
                </ClinicalModalMetaItem>

                <ClinicalModalMetaItem>
                  ·
                </ClinicalModalMetaItem>

                <ClinicalModalMetaItem>
                  Prof. asignada: {getProfesionalSesion(cambio)}
                </ClinicalModalMetaItem>
              </ClinicalModalMetaRow>
            </ClinicalModalHeader>

            <form onSubmit={(e) => modificarSesion(e)}>
              <ClinicalModalBody>
                <ClinicalModalNotice>
                  Los datos del turno se editan desde Calendario. Acá se edita/guarda la información clínica de esta entrada.
                </ClinicalModalNotice>

                <ClinicalModalSectionLabel>
                  Datos del turno
                </ClinicalModalSectionLabel>

                <ClinicalModalGrid>
                  <ClinicalModalField>
                    <ClinicalModalLabel>Fecha</ClinicalModalLabel>
                    <ClinicalModalReadOnly>
                      {formatFechaParaMostrar(getFechaSesion(cambio))}
                    </ClinicalModalReadOnly>
                  </ClinicalModalField>

                  <ClinicalModalField>
                    <ClinicalModalLabel>Horario</ClinicalModalLabel>
                    <ClinicalModalReadOnly>
                      {getHoraSesion(cambio)}
                    </ClinicalModalReadOnly>
                  </ClinicalModalField>

                  <ClinicalModalField>
                    <ClinicalModalLabel>Modalidad</ClinicalModalLabel>
                    <ClinicalModalReadOnly>
                      {getModalidadSesion(cambio)}
                    </ClinicalModalReadOnly>
                  </ClinicalModalField>

                  <ClinicalModalField>
                    <ClinicalModalLabel>Lugar</ClinicalModalLabel>
                    <ClinicalModalReadOnly>
                      {getLugarSesion(cambio)}
                    </ClinicalModalReadOnly>
                  </ClinicalModalField>

                  <ClinicalModalField>
                    <ClinicalModalLabel>N° sesión</ClinicalModalLabel>
                    <ClinicalModalReadOnly>
                      {cambio.sesion || '—'}
                    </ClinicalModalReadOnly>
                  </ClinicalModalField>

                  <ClinicalModalField>
                    <ClinicalModalLabel>Estado</ClinicalModalLabel>
                    <ClinicalModalReadOnly>
                      {getEstadoMeta(cambio).label}
                    </ClinicalModalReadOnly>
                  </ClinicalModalField>
                </ClinicalModalGrid>

                <ClinicalModalSectionLabel>
                  Registro clínico
                </ClinicalModalSectionLabel>

                {renderCamposDinamicos()}
              </ClinicalModalBody>

              <ClinicalModalFooter>
                <ClinicalModalFooterHint>
                  Se guarda en la Historia Clínica del paciente.
                </ClinicalModalFooterHint>

                <ClinicalModalActions>
                  <ClinicalCancelButton
                    type="button"
                    onClick={cerrarModalEdicion}
                    disabled={guardandoHistoriaClinica}
                  >
                    Cancelar
                  </ClinicalCancelButton>

                  <ClinicalSaveButton
                    type="submit"
                    disabled={guardandoHistoriaClinica}
                  >
                    {guardandoHistoriaClinica ? 'Guardando...' : 'Guardar'}
                  </ClinicalSaveButton>
                </ClinicalModalActions>
              </ClinicalModalFooter>
            </form>
          </ClinicalModalCard>
        ) : null}
      </Modal>
    </Cuerpo>
  );
};

export default HistoriaClinica;